import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import GenesisAccountCreator from './GenesisAccountCreator'; 
import LeadIntakeForm from './LeadIntakeForm'; 
import { downloadCSV } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantLeads } from '../../services/crmService'; 

// --- ENTERPRISE DIVISION MAPPING ---
// This maps raw package/service types into the 7 Reinhardt SaaS Divisions
const DIVISION_MAP = {
  'apex_os': 'Apex Retail Suite',
  'operations': 'Operations Hub',
  'eekopay': 'eeKoPay Financial',
  'hardware': 'Hardware & Assets',
  'consulting': 'Consulting & Strategy',
  'telemetry': 'Telemetry & Data',
  'other': 'General / Misc'
};

const DIVISIONS = ['ALL NETWORK', ...Object.values(DIVISION_MAP)];

export default function NetworkLedger({ isOperatorView = false }) {
  const [showGenesisIntake, setShowGenesisIntake] = useState(false);
  const [showLeadIntake, setShowLeadIntake] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser, isFounder } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [activeDivision, setActiveDivision] = useState('ALL NETWORK'); 

  const [ledgerData, setLedgerData] = useState([]);

  // --- STAGE & STATUS CONFIGURATION ---
  const stageConfig = {
    closedWon: { label: 'Active Tenant', color: 'border-emerald-500 text-emerald-600 bg-emerald-50' },
    negotiating: { label: 'In Negotiation', color: 'border-blue-500 text-blue-600 bg-blue-50' },
    contacted: { label: 'Contacted', color: 'border-amber-500 text-amber-600 bg-amber-50' },
    newLeads: { label: 'Pipeline Lead', color: 'border-slate-400 text-slate-600 bg-slate-50' },
    dealDesk: { label: 'Deal Desk Review', color: 'border-indigo-500 text-indigo-600 bg-indigo-50' }
  };

  // --- SECURE DATA FETCH & DOWNLINE ROUTING ---
  useEffect(() => {
    const fetchLedger = async () => {
      if (tenantLoading || !tenantId) return;

      setIsLoading(true);
      try {
        let rawDeals = await getTenantLeads(tenantId);

        // 1. Downline Enforcer: If not founder, isolate to their specific network tree
        if (!isFounder && isOperatorView) {
          rawDeals = rawDeals.filter(deal => {
            // Check if they are the direct rep OR the master agent for the deal
            return deal.rep_uid === currentUser.uid || deal.master_agent_id === currentUser.uid;
          });
        }

        // 2. Real-Time Data Parsing
        const formattedLedger = rawDeals.map(deal => {
          let rawStatus = deal.stage || deal.status || 'newLeads';
          let stage = rawStatus;
          
          if (rawStatus === 'Paid Lead - Unscheduled' || rawStatus === 'New Lead') stage = 'newLeads';
          if (rawStatus === 'Deal Desk Review') stage = 'dealDesk';

          const config = stageConfig[stage] || stageConfig.newLeads;
          
          // Authentic Timestamp Resolution
          let touchDate = 'N/A';
          const targetDate = deal.updatedAt || deal.last_touch || deal.createdAt;
          if (targetDate?.toDate) {
            touchDate = targetDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          } else if (typeof targetDate === 'string') {
            touchDate = new Date(targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          }

          const personalName = deal.firstName ? `${deal.firstName} ${deal.lastName || ''}`.trim() : null;
          const assignedDivision = DIVISION_MAP[deal.packageType] || DIVISION_MAP['other'];
          
          return {
            id: deal.id, 
            displayId: (deal.dealerId || deal.id.substring(0, 8)).toUpperCase(), 
            entity: deal.company || deal.entityName || deal.company_name || personalName || 'Unknown Entity',
            contact: deal.primaryContact || deal.contact_name || personalName || 'No Contact',
            email: deal.contact_email || deal.email || '',
            phone: deal.contact_phone || deal.phone || '',
            status: config.label,
            rawStage: stage,
            mrr: Number(deal.mrr_value || deal.mrr || deal.amountPaid || 0),
            lastAction: touchDate,
            rep: deal.rep_name || (deal.rep_uid ? 'Unassigned' : 'System Auto-Route'),
            statusColor: config.color,
            division: assignedDivision,
            rawPackageType: deal.packageType || 'other'
          };
        });

        // Sort by highest MRR to highlight VIPs
        formattedLedger.sort((a, b) => b.mrr - a.mrr);
        setLedgerData(formattedLedger);
      } catch (error) {
        console.error("Failed to fetch ledger data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      fetchLedger();
    }
  }, [currentUser, isOperatorView, isFounder, tenantId, tenantLoading]);

  // --- ENTERPRISE FILTERING ENGINE ---
  const displayData = useMemo(() => {
    return ledgerData.filter(item => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        item.entity.toLowerCase().includes(query) || 
        item.contact.toLowerCase().includes(query) || 
        item.displayId.toLowerCase().includes(query) ||
        item.email.toLowerCase().includes(query) ||
        item.rep.toLowerCase().includes(query);
      
      let matchesStatus = true;
      if (statusFilter === 'ACTIVE') matchesStatus = item.rawStage === 'closedWon';
      if (statusFilter === 'PIPELINE') matchesStatus = item.rawStage !== 'closedWon';
      if (statusFilter === 'DEAL DESK') matchesStatus = item.rawStage === 'dealDesk';

      let matchesDivision = true;
      if (activeDivision !== 'ALL NETWORK') {
        matchesDivision = item.division === activeDivision;
      }

      return matchesSearch && matchesStatus && matchesDivision;
    });
  }, [ledgerData, searchQuery, statusFilter, activeDivision]);

  // --- LIVE FINANCIAL ROLL-UPS ---
  const metrics = useMemo(() => {
    let totalMrr = 0;
    let pipelineValue = 0;
    let activeAccounts = 0;

    displayData.forEach(deal => {
      if (deal.rawStage === 'closedWon') {
        totalMrr += deal.mrr;
        activeAccounts++;
      } else {
        pipelineValue += deal.mrr;
      }
    });

    return { totalMrr, pipelineValue, activeAccounts };
  }, [displayData]);

  const handleExport = () => {
    downloadCSV(displayData, isOperatorView ? 'My_Downline_Ledger.csv' : 'Global_Network_Ledger.csv');
  };

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative font-sans">
      
      {/* HEADER */}
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">
            {isOperatorView ? "My Network Downline" : "Master Network Ledger"}
          </h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">
            {isOperatorView 
              ? "Financial ledger and operational directory for your portfolio." 
              : "Global aggregate directory and financial ledger across all SaaS divisions."}
          </p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={handleExport}
            className="text-[10px] font-bold text-slate-500 hover:text-slate-900 uppercase tracking-widest transition-colors px-4 py-2 border border-slate-300 bg-white shadow-sm flex items-center gap-2 rounded-sm"
          >
            <i className="fa-solid fa-download"></i> Export Ledger
          </button>

          {isOperatorView ? (
            <button 
              onClick={() => setShowLeadIntake(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold py-2 px-6 transition-colors uppercase tracking-widest shadow-sm flex items-center gap-2 rounded-sm"
            >
              <i className="fa-solid fa-plus"></i> Submit New Deal
            </button>
          ) : (
            <button 
              onClick={() => setShowGenesisIntake(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-2 px-6 transition-colors uppercase tracking-widest border border-slate-900 shadow-sm flex items-center gap-2 rounded-sm"
            >
              <i className="fa-solid fa-bolt text-amber-400"></i> Provision Account
            </button>
          )}
        </div>
      </div>

      {/* EXECUTIVE SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-300 p-5 shadow-sm rounded-sm">
          <div className="flex justify-between items-start mb-2">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Active Yield (MRR)</p>
            <i className="fa-solid fa-money-bill-trend-up text-emerald-500"></i>
          </div>
          <h3 className="text-3xl font-black text-slate-900">
            ${metrics.totalMrr.toLocaleString(undefined, {minimumFractionDigits: 0})}
          </h3>
          <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest mt-2 bg-emerald-50 inline-block px-2 py-1 rounded-sm">
            Collected Monthly
          </p>
        </div>
        
        <div className="bg-white border border-slate-300 p-5 shadow-sm rounded-sm">
          <div className="flex justify-between items-start mb-2">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Pipeline Value</p>
            <i className="fa-solid fa-filter-circle-dollar text-indigo-500"></i>
          </div>
          <h3 className="text-3xl font-black text-slate-900">
            ${metrics.pipelineValue.toLocaleString(undefined, {minimumFractionDigits: 0})}
          </h3>
          <p className="text-[9px] font-bold text-indigo-600 uppercase tracking-widest mt-2 bg-indigo-50 inline-block px-2 py-1 rounded-sm">
            Pending / Negotiating
          </p>
        </div>

        <div className="bg-white border border-slate-300 p-5 shadow-sm rounded-sm">
          <div className="flex justify-between items-start mb-2">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Network Entities</p>
            <i className="fa-solid fa-network-wired text-blue-500"></i>
          </div>
          <h3 className="text-3xl font-black text-slate-900">
            {metrics.activeAccounts}
          </h3>
          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-2 bg-slate-100 inline-block px-2 py-1 rounded-sm">
            Active Accounts
          </p>
        </div>
      </div>

      {/* DIVISION TABS */}
      <div className="flex overflow-x-auto custom-scrollbar mb-6 gap-2 pb-2">
        {DIVISIONS.map(division => (
          <button
            key={division}
            onClick={() => setActiveDivision(division)}
            className={`whitespace-nowrap px-5 py-2 text-[10px] font-black uppercase tracking-widest transition-all rounded-full border ${
              activeDivision === division 
                ? 'bg-slate-900 text-white border-slate-900 shadow-md' 
                : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400 hover:bg-slate-50'
            }`}
          >
            {division}
          </button>
        ))}
      </div>

      {/* SEARCH & FILTER BAR */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SEARCH BY ENTITY, CONTACT, ID, OR REP..." 
            className="w-full bg-white border border-slate-300 text-slate-800 text-sm font-bold pl-10 pr-4 py-3 focus:outline-none focus:border-emerald-500 rounded-sm placeholder:text-slate-400 placeholder:font-bold placeholder:uppercase placeholder:text-[10px] placeholder:tracking-widest"
          />
        </div>
        <select 
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-slate-300 text-slate-700 text-[10px] font-bold px-4 py-3 uppercase tracking-widest focus:outline-none focus:border-emerald-500 cursor-pointer w-48 shadow-sm rounded-sm"
        >
          <option value="ALL">STATUS: ALL</option>
          <option value="ACTIVE">ACTIVE TENANTS</option>
          <option value="PIPELINE">PIPELINE (ALL)</option>
          <option value="DEAL DESK">DEAL DESK PENDING</option>
        </select>
      </div>

      {/* ENTERPRISE DATA TABLE */}
      <div className="bg-white border border-slate-300 shadow-sm rounded-sm overflow-hidden">
        <div className="bg-slate-900 border-b border-slate-800 px-6 py-4 grid grid-cols-12 gap-4 items-center">
          <div className="col-span-1 text-[10px] font-black text-slate-400 uppercase tracking-widest">ID</div>
          <div className="col-span-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Entity & Division</div>
          <div className="col-span-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Primary Contact</div>
          <div className="col-span-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Account Status</div>
          <div className="col-span-1 text-[10px] font-black text-slate-400 uppercase tracking-widest">MRR Yield</div>
          <div className="col-span-2 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Last Audit</div>
        </div>
        
        <div className="divide-y divide-slate-100">
          {(isLoading || tenantLoading) && (
            <div className="p-16 flex flex-col items-center justify-center bg-slate-50/50">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Synchronizing Master Ledger...</p>
            </div>
          )}

          {!(isLoading || tenantLoading) && displayData.map((row) => (
            <Link key={row.id} to={`/customer/${row.id}`} className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors group cursor-pointer relative">
              <div className="col-span-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {row.displayId}
              </div>
              
              <div className="col-span-4 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-black text-slate-900 group-hover:text-emerald-700 transition-colors block truncate">{row.entity}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[8px] font-black bg-slate-100 text-slate-600 px-1.5 py-0.5 uppercase tracking-widest rounded-sm border border-slate-200 flex-shrink-0">
                    {row.division}
                  </span>
                  {!isOperatorView && (
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate block">
                      Rep: {row.rep}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-span-2 text-xs text-slate-700 font-bold truncate pr-2">
                {row.contact}
              </div>
              
              <div className="col-span-2">
                <span className={`border text-[9px] font-black px-2 py-1 uppercase tracking-widest rounded-sm inline-block ${row.statusColor}`}>
                  {row.status}
                </span>
              </div>
              
              <div className="col-span-1 text-sm font-black text-slate-800">
                ${row.mrr.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
              </div>
              
              <div className="col-span-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right group-hover:text-emerald-600 transition-colors flex items-center justify-end gap-2">
                {row.lastAction}
                <i className="fa-solid fa-chevron-right opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </div>
            </Link>
          ))}
          
          {!(isLoading || tenantLoading) && displayData.length === 0 && (
            <div className="px-4 py-16 text-center bg-slate-50">
              <i className="fa-solid fa-folder-open text-3xl text-slate-300 mb-3 block"></i>
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">No accounts found matching current network filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      {showGenesisIntake && !isOperatorView && <GenesisAccountCreator onClose={() => setShowGenesisIntake(false)} />}
      
      {showLeadIntake && (
        <LeadIntakeForm 
          isOperatorView={isOperatorView} 
          onClose={() => { 
            setShowLeadIntake(false); 
          }} 
        />
      )}
    </div>
  );
}